import { apiInstance } from '@diamond/shared/utils';
import { AxiosError } from 'axios';

/**
 * Creates a wrapper for making PUT requests to an API endpoint with error handling.
 *
 * @param url - The base URL endpoint for the PUT request (without trailing slash)
 * @param token - Optional authentication token to be used with the API request
 *
 * @returns Object containing the putData function for making PUT requests
 *
 * @example
 * ```typescript
 * const { putData } = PutDataWrapper('/api/users', 'your-auth-token');
 *
 * // Update user with ID 123
 * const result = await putData({ name: 'John', email: 'john@example.com' }, 123);
 *
 * // Update without specific ID (uses base URL)
 * const result = await putData({ settings: { theme: 'dark' } });
 * ```
 */
export function PutDataWrapper(url: string, token?: string) {
  /**
   * Makes a PUT request to the configured endpoint with the provided payload.
   *
   * @template T - The type of the payload being sent
   * @param payload - The data to be sent in the PUT request body
   * @param id - Optional ID to append to the URL path (e.g., /api/users/123)
   *   If not provided, uses the base URL
   *
   * @returns Promise resolving to the API response data (unknown type)
   *
   * @throws Error with the API error message if the request fails
   *
   * @example
   * ```typescript
   * // Update specific resource by ID
   * const result = await putData({ name: 'John', age: 30 }, 123);
   * // Request URL: /api/users/123
   *
   * // Update using base URL without ID
   * const result = await putData({ settings: { theme: 'dark' } });
   * // Request URL: /api/users
   * ```
   */
  async function putData<T>(
    payload: T,
    id?: number | string
  ): Promise<unknown> {
    const putUrl = id ? `${url}/${id}` : `${url}`;
    try {
      const response = await apiInstance(token).put(
        putUrl,
        JSON.stringify({ id, ...payload })
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data.message);
      }
      return;
    }
  }
  return { putData };
}
